import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useState } from 'react'
import { RenderFormat } from '@/content-viewer/store/schemas'
import { loadUserFileMarkdownHtml, loadUserFilePdf } from '../utils/load-user-file-viewable-content'

type UseUserFileViewableContentOptions = {
  userFileId: string
  format: RenderFormat.PDF | RenderFormat.MARKDOWN
  title?: string
  markdownHighlights?: string[] | null
}

type UseUserFileViewableContentReturn = {
  loading: boolean
  error: boolean
  pdfFile: File | null
  htmlString: string | null
  retry: () => void
}

export function useUserFileViewableContent(options: UseUserFileViewableContentOptions): UseUserFileViewableContentReturn {
  const { userFileId, format, title, markdownHighlights } = options
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [htmlString, setHtmlString] = useState<string | null>(null)
  const [refetchCount, setRefetchCount] = useState(0)

  const retry = useCallback(() => {
    setRefetchCount((previous) => previous + 1)
  }, [])

  useEffect(() => {
    let cancelled = false

    async function loadContent() {
      setLoading(true)
      setError(false)
      setPdfFile(null)
      setHtmlString(null)

      try {
        if (format === RenderFormat.PDF) {
          const nextPdfFile = await loadUserFilePdf({ userFileId, title })
          if (!cancelled) setPdfFile(nextPdfFile)
          return
        }

        const nextHtmlString = await loadUserFileMarkdownHtml({ userFileId, highlightSentences: markdownHighlights })
        if (!cancelled) setHtmlString(nextHtmlString)
      } catch (e) {
        if (cancelled) return
        Sentry.captureException(e, {
          extra: {
            userFileId,
            format,
            title,
          },
        })
        setError(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadContent()

    return () => {
      cancelled = true
    }
  }, [format, markdownHighlights, refetchCount, title, userFileId])

  return { loading, error, pdfFile, htmlString, retry }
}
